import { Badge } from '@/components/ui/badge';
import { cn } from '@/utils/helpers';
import { FileText, Image as ImageIcon } from 'lucide-react';
import { useMemo } from 'react';
import { ChatAiIcon } from './ChatAiIcon';
import { ChatAiLoadingIcon } from './ChatAiLoadingIcon';
import { ChatAttachment } from './ChatAttachment';
import { ChatMessageActions } from './ChatMessageActions';
import { ChatAiFormatter } from './formatter/ChatAiFormatter';
import { AutoContinuationPrompt } from './AutoContinuationPrompt';
import type { Message, PrinceAttachment } from './types';
import { MessageType } from './types';
import { UserAbortConfirmation } from './UserAbortConfirmation';
import { stripXmlBlocks } from './utils/stripXmlBlocks';

// Helper function to extract filenames from XML blocks
function extractAttachedFiles(content: string): { filename: string; type: 'document' | 'image' }[] {
  const files: { filename: string; type: 'document' | 'image' }[] = [];

  // Extract document filenames
  const documentMatches = content.matchAll(/<document[^>]*filename="([^"]*)"[^>]*>/g);
  for (const match of documentMatches) {
    files.push({ filename: match[1], type: 'document' });
  }

  // Extract image filenames
  const imageMatches = content.matchAll(/<image[^>]*filename="([^"]*)"[^>]*>/g);
  for (const match of imageMatches) {
    files.push({ filename: match[1], type: 'image' });
  }

  return files;
}
interface ChatMessageProps {
  message: Message;
  onAttachmentClick: (attachment: PrinceAttachment) => void;
  onThumbsUp?: () => Promise<void>;
  onThumbsDown?: (comment?: string) => Promise<void>;
  onRefresh?: () => Promise<void>;
  onCopy?: () => Promise<void>;
  onContinueAuto?: () => Promise<void>;
}

export function ChatMessage({
  message,
  onAttachmentClick,
  onThumbsUp,
  onThumbsDown,
  onRefresh,
  onCopy,
  onContinueAuto,
}: ChatMessageProps) {
  const isHuman = message.type === MessageType.USER;
  // Only extract files if the message actually contains XML blocks
  const hasXmlBlocks =
    isHuman && (message.content.includes('<document') || message.content.includes('<image'));
  const attachedFiles = useMemo(() => {
    return hasXmlBlocks ? extractAttachedFiles(message.content) : [];
  }, [hasXmlBlocks, message.content]);

  return (
    <div className="flex gap-3 mb-6 w-full">
      {!isHuman && (
        <div className="pt-0.5">
          <ChatAiIcon />
        </div>
      )}

      <div className={cn('flex-1 flex w-full', isHuman ? 'justify-end' : 'justify-start')}>
        <div
          className={cn(
            'text-text-primary',
            isHuman
              ? 'text-text-white bg-gray-dark-0 max-w-[80%] max-[800px]:max-w-full rounded-xl rounded-tr-none p-4'
              : 'w-full'
          )}
        >
          <div className="flex flex-col text-sm leading-relaxed">
            {isHuman ? (
              <div>{stripXmlBlocks(message.content)}</div>
            ) : (
              <ChatAiFormatter content={message.content} />
            )}
            {message.type === MessageType.AI_AGENT && message.isStreaming && <ChatAiLoadingIcon />}

            {/* Abort UI Block */}
            {message.type === MessageType.AI_AGENT && message.isAborted && (
              <UserAbortConfirmation />
            )}

            {message.type === MessageType.AI_AGENT && message.autoContinuation?.limitReached && (
              <AutoContinuationPrompt
                attempts={message.autoContinuation.continuationAttempts}
                maxAttempts={message.autoContinuation.maxAutoContinuations}
                onContinue={onContinueAuto}
              />
            )}
          </div>

          {/* Show filename pills for human messages with attachments */}
          {isHuman && attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {attachedFiles.map((file, idx) => (
                <Badge
                  key={`${file.type}-${idx}`}
                  variant={file.type === 'document' ? 'secondary' : 'outline'}
                  className="flex items-center gap-1 text-xs"
                >
                  {file.type === 'document' ? (
                    <FileText className="h-3 w-3" />
                  ) : (
                    <ImageIcon className="h-3 w-3" />
                  )}
                  {file.filename}
                </Badge>
              ))}
            </div>
          )}

          {/* Attachments */}
          {message.type === MessageType.AI_AGENT &&
            !message.isStreaming &&
            message.attachments &&
            message.attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {message.attachments.map(attachment => (
                  <ChatAttachment
                    key={attachment.id}
                    attachment={attachment}
                    onClick={() => onAttachmentClick(attachment)}
                  />
                ))}
              </div>
            )}

          {message.type === MessageType.AI_AGENT && !message.isStreaming && (
            <ChatMessageActions
              onThumbsUp={onThumbsUp}
              onThumbsDown={onThumbsDown}
              onRefresh={onRefresh}
              onCopy={onCopy}
              rating={message.userRating}
            />
          )}
        </div>
      </div>
    </div>
  );
}
